/**
 * Container Actions Component
 *
 * Provides icon-only action buttons for container management with tooltips:
 * - Evaluate Documents with AI
 * - Report Issue
 * - View Risk Assessment
 * - Refresh Tracking (controlled by platform setting)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    SearchRefraction,
    RefreshCw01,
    AlertTriangle,
    TrendUp01,
    Loading02,
} from '@untitledui/icons';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';

// Modal components
import { EvaluateDocumentsModal } from './modals/evaluate-documents-modal';
import { ReportIssueModal } from './modals/report-issue-modal';
import { RiskAssessmentModal } from './modals/risk-assessment-modal';

interface ContainerActionsProps {
    containerId: string;
    containerNumber: string;
    className?: string;
    onActionComplete?: () => void;
    enableRefresh?: boolean;
    onRefreshTracking?: () => void;
    isRefreshing?: boolean;
}

export function ContainerActions({
    containerId,
    containerNumber,
    className = '',
    onActionComplete,
    enableRefresh = false,
    onRefreshTracking,
    isRefreshing = false,
}: ContainerActionsProps) {
    const { t } = useTranslation('pages:containers');

    // Modal states
    const [showEvaluateModal, setShowEvaluateModal] = useState(false);
    const [showReportIssueModal, setShowReportIssueModal] = useState(false);
    const [showRiskModal, setShowRiskModal] = useState(false);

    // Icon button base styles
    const iconButtonStyles = "flex items-center justify-center size-10 rounded-lg bg-primary shadow-xs-skeumorphic ring-1 ring-primary ring-inset text-tertiary hover:text-primary hover:bg-primary_hover transition-colors";
    const disabledIconButtonStyles = "flex items-center justify-center size-10 rounded-lg bg-secondary/50 ring-1 ring-secondary text-tertiary/50 cursor-not-allowed";

    return (
        <>
            {/* Action Buttons - Icon only with tooltips */}
            <div className={`flex items-center gap-2 ${className}`}>
                {/* Evaluate Documents */}
                <Tooltip
                    title={t('detail.actions.evaluateDocs', 'Evaluate Documents')}
                    description={t('detail.actions.evaluateDocsDesc', 'AI cross-check for discrepancies')}
                >
                    <TooltipTrigger onPress={() => setShowEvaluateModal(true)}>
                        <span className={iconButtonStyles}>
                            <SearchRefraction className="size-5" />
                        </span>
                    </TooltipTrigger>
                </Tooltip>

                {/* Report Issue */}
                <Tooltip
                    title={t('detail.actions.reportIssue', 'Report Issue')}
                    description={t('detail.actions.reportIssueDesc', 'Report a problem with this container')}
                >
                    <TooltipTrigger onPress={() => setShowReportIssueModal(true)}>
                        <span className={iconButtonStyles}>
                            <AlertTriangle className="size-5" />
                        </span>
                    </TooltipTrigger>
                </Tooltip>

                {/* View Risk Assessment */}
                <Tooltip
                    title={t('detail.actions.viewRisk', 'Risk Assessment')}
                    description={t('detail.actions.viewRiskDesc', 'View detailed risk analysis')}
                >
                    <TooltipTrigger onPress={() => setShowRiskModal(true)}>
                        <span className={iconButtonStyles}>
                            <TrendUp01 className="size-5" />
                        </span>
                    </TooltipTrigger>
                </Tooltip>

                {/* Refresh Tracking */}
                {enableRefresh && (
                    <Tooltip
                        title={t('detail.actions.refreshTracking', 'Refresh Tracking')}
                        description={t('detail.actions.refreshTrackingDesc', 'Sync latest tracking data from JSONCargo')}
                    >
                        <TooltipTrigger
                            onPress={() => !isRefreshing && onRefreshTracking?.()}
                            isDisabled={isRefreshing}
                        >
                            <span className={isRefreshing ? disabledIconButtonStyles : iconButtonStyles}>
                                {isRefreshing ? (
                                    <Loading02 className="size-5 animate-spin" />
                                ) : (
                                    <RefreshCw01 className="size-5" />
                                )}
                            </span>
                        </TooltipTrigger>
                    </Tooltip>
                )}
            </div>

            {/* Modals */}
            {showEvaluateModal && (
                <EvaluateDocumentsModal
                    containerId={containerId}
                    containerNumber={containerNumber}
                    isOpen={showEvaluateModal}
                    onClose={() => setShowEvaluateModal(false)}
                    onSuccess={() => {
                        setShowEvaluateModal(false);
                        onActionComplete?.();
                    }}
                />
            )}

            {showReportIssueModal && (
                <ReportIssueModal
                    containerId={containerId}
                    containerNumber={containerNumber}
                    isOpen={showReportIssueModal}
                    onClose={() => setShowReportIssueModal(false)}
                    onSuccess={() => {
                        setShowReportIssueModal(false);
                        onActionComplete?.();
                    }}
                />
            )}

            {showRiskModal && (
                <RiskAssessmentModal
                    containerId={containerId}
                    containerNumber={containerNumber}
                    isOpen={showRiskModal}
                    onClose={() => setShowRiskModal(false)}
                />
            )}
        </>
    );
}
